// Ported from Octo.Mobile@79a7c69 src/utils/formatters.ts
import { formatDateDisplay, formatDateTimeDisplay, formatTimeDisplay } from './dateValue';
import { formatNumberDotNet } from './data/dotnetFormat';

/** Tarih (dd.MM.yyyy) — "YYYY-MM-DD" yerel gün olarak okunur, dilim kayması olmaz */
export function formatDate(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  return formatDateDisplay(value);
}

/** Tarih + saat (dd.MM.yyyy HH:mm) */
export function formatDateTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  return formatDateTimeDisplay(value);
}

/** Saat (HH:mm) */
export function formatTime(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  return formatTimeDisplay(value);
}

/**
 * Format a number with optional scale (decimal places).
 */
export function formatNumber(value: unknown, scale?: number | null): string {
  if (value === null || value === undefined) return '';
  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num)) return String(value);

  if (scale !== null && scale !== undefined) {
    return num.toLocaleString('tr-TR', {
      minimumFractionDigits: scale,
      maximumFractionDigits: scale,
    });
  }
  return num.toLocaleString('tr-TR');
}

/**
 * Format a boolean value.
 */
export function formatBoolean(value: unknown): string {
  if (value === true || value === 1 || value === '1' || value === 'true') return 'Evet';
  if (value === false || value === 0 || value === '0' || value === 'false') return 'Hayır';
  return '';
}

const INT_DB_TYPES = new Set(['int', 'bigint', 'smallint', 'tinyint']);
const DECIMAL_DB_TYPES = new Set(['decimal', 'numeric', 'money', 'float', 'real']);

/**
 * Kimlik alanı mı (iş emri / fiş / sipariş no): birincil anahtar ya da adı Id / No ile
 * biten tamsayı alan. Bu alanlar hesaplanan değil, okunan / aranan / kopyalanan
 * numaralar; binlik ayırıcıyla (1.325) gösterilince aramada, Excel'e yapıştırmada ve
 * basılı etiketle karşılaştırmada eşleşmiyordu (#538).
 */
export function isIdentityField(f: {
  name: string;
  primaryKey?: boolean | null;
  fieldDbTypeName?: string | null;
}): boolean {
  if (!INT_DB_TYPES.has((f.fieldDbTypeName ?? '').toLowerCase())) return false;
  return !!f.primaryKey || /(?:Id|ID|No|NO)$/.test(f.name);
}

export interface FormatCellOptions {
  /** Kimlik alanı: DisplayFormat boşsa ayırıcısız yazılır (bkz. isIdentityField). */
  identity?: boolean;
}

/**
 * Format a cell value based on editor type and/or DB type.
 *
 * Sayısal alanda sıra (#538): alanın DisplayFormat'ı doluysa her zaman o (.NET kalıbı:
 * `#,##0.######`, `N2`, `0`…); boşsa kimlik alanı ayırıcısız; diğer sayılar varsayılan
 * (binlik ayırıcı).
 */
export function formatCellValue(
  value: unknown,
  editorTypeName: string | null,
  displayFormat: string | null,
  scale?: number | null,
  fieldDbTypeName?: string | null,
  opts?: FormatCellOptions,
): string {
  if (value === null || value === undefined) return '';

  const dbType = (fieldDbTypeName ?? '').toLowerCase();
  const numeric = editorTypeName === 'SpinEdit' || INT_DB_TYPES.has(dbType) || DECIMAL_DB_TYPES.has(dbType);
  if (numeric && value !== '') {
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isNaN(num)) {
      if (displayFormat?.trim()) return formatNumberDotNet(num, displayFormat);
      if (opts?.identity && Number.isInteger(num)) return String(num);
    }
  }

  // 1. Format by editorTypeName (highest priority)
  switch (editorTypeName) {
    case 'DateEdit':
      return formatDate(value);
    case 'DateTimeEdit':
      return formatDateTime(value);
    case 'TimeEdit':
      return formatTime(value);
    case 'CheckEdit':
      return formatBoolean(value);
    case 'SpinEdit':
      return formatNumber(value, scale);
  }

  // 2. Fallback: format by fieldDbTypeName
  if (fieldDbTypeName) {
    const dbType = fieldDbTypeName.toLowerCase();
    if (dbType === 'datetime' || dbType === 'datetime2' || dbType === 'smalldatetime') {
      return formatDateTime(value);
    }
    if (dbType === 'date') {
      return formatDate(value);
    }
    if (dbType === 'time') {
      return formatTime(value);
    }
    if (dbType === 'bit') {
      return formatBoolean(value);
    }
    if (dbType === 'int' || dbType === 'bigint' || dbType === 'smallint' || dbType === 'tinyint') {
      return formatNumber(value, 0);
    }
    if (
      dbType === 'decimal' ||
      dbType === 'numeric' ||
      dbType === 'money' ||
      dbType === 'float' ||
      dbType === 'real'
    ) {
      return formatNumber(value, scale);
    }
  }

  return String(value);
}

/**
 * Resolve the card title for a given row — mirrors MasterRecordCard title logic.
 * Priority: label columns joined with ' | ' → displayFieldName → first visible column → fallback.
 */
export function resolveCardTitle(
  row: Record<string, unknown>,
  columns: Array<{
    name: string;
    visible: boolean;
    displayOrder: number;
    dataType: string;
    label?: boolean;
    editorTypeName: string | null;
    displayFormat: string | null;
    scale: number | null;
    fieldDbTypeName: string | null;
  }>,
  displayFieldName: string | null | undefined,
  fallback: string,
): string {
  const visibleCols = columns
    .filter((c) => c.visible && c.dataType !== 'guid' && c.dataType !== 'binary')
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const labelCols = visibleCols.filter((c) => c.label);

  if (labelCols.length > 0) {
    const val = labelCols
      .map((col) =>
        formatCellValue(row[col.name], col.editorTypeName, col.displayFormat, col.scale, col.fieldDbTypeName),
      )
      .filter((v) => v != null && v !== '')
      .join(' | ');
    return val || fallback;
  }

  const titleCol = displayFieldName
    ? visibleCols.find((c) => c.name.toLowerCase() === displayFieldName.toLowerCase()) || visibleCols[0]
    : visibleCols[0];

  if (titleCol) {
    const val = formatCellValue(
      row[titleCol.name],
      titleCol.editorTypeName,
      titleCol.displayFormat,
      titleCol.scale,
      titleCol.fieldDbTypeName,
    );
    return val || fallback;
  }

  return fallback;
}
